import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useSelector, shallowEqual, useDispatch } from 'dva'
import { CategoryPicker, ProductCard, CategoryTitle } from 'components'
import { searchProps } from 'components/searchbar'
import { Breadcrumb, Spin, Pagination as AntdPagination, Empty } from 'antd'
import { Sort, Pagination, More } from './components'
import qs from 'query-string'
import {
  deepFilterCategoryItem,
  requestOrderEnums,
  deepFindCategoryChildren,
} from './config'
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb'
import styles from './index.module.less'

type Props = {}

const selector = ({ home, search, loading }) => ({
  list: search.list || [],
  total: search.total,
  pageSize: search.pageSize,
  categoryList: home.categoryList || [],
  loading: loading.effects['search/searchUsingPOST'],
})

const Search = (props: Props) => {
  const dispatch = useDispatch()
  const { pageSize, list, total, categoryList, loading } = useSelector(
    selector,
    shallowEqual
  )
  const history = useHistory()
  const location = useLocation()

  const [breadcrumbItems, setBreadcrumbItems] = useState<ItemType[]>([])
  const [moreCategoryList, setMoreCategoryList] = useState<
    SwaggerDTO.ProductConfigSpuInfoDTO[]
  >([])

  // 搜索相关信息
  const searchData = useMemo<searchProps>(() => {
    return qs.parse(location.search)
  }, [location])

  // 处理面包屑以及分类
  const breadcrumbInit = useCallback(() => {
    const productItem = deepFilterCategoryItem(categoryList, searchData.c)
    // 设置分类
    if (productItem?.childList?.length) {
      setMoreCategoryList([
        { productCode: 'all', product: '全部' } as any,
        ...productItem?.childList,
      ])
    }
    let list: ItemType[] = [{ title: '全部' }]

    // 类目编码
    const productCodes = productItem?.productFullCode?.split('/')
    // 类目名称
    const productName = productItem?.productFullPath?.split('/')

    productCodes?.forEach((item, index) => {
      list.push({
        title: (
          <CategoryTitle code={item}>{productName?.[index]}</CategoryTitle>
        ),
      })
    })
    // 没有类目时就是关键字查询需要展示关键字
    if (!searchData.c && searchData.w) {
      list.push({ title: searchData.w })
    }
    setBreadcrumbItems([...list])
  }, [categoryList, searchData])

  // 获取列表
  const queryList = useCallback(
    (params = {}) => {
      if (!categoryList.length) return
      const category = deepFilterCategoryItem(
        categoryList,
        searchData.cc || searchData.c
      )

      let categoryFilterList = deepFindCategoryChildren(
        category ? [category] : categoryList
      )
      let frontCategoryCode: Array<string> = categoryFilterList?.map(
        c => c.productCode
      )

      dispatch({
        type: 'search/searchUsingPOST',
        payload: {
          frontCategoryCodes: frontCategoryCode,
          frontCategoryCode,
          searchType: searchData.type,
          keyWord: searchData.w,
          pageNum: +(searchData?.page || 0),
          pageSize,
          order: requestOrderEnums[searchData?.sort || 0],
          ...params,
        },
      })
    },
    [dispatch, searchData, pageSize, categoryList]
  )

  // 跳转处理
  const JumpHandle = params => {
    history.push(`/search?${qs.stringify({ ...searchData, ...params })}`)
  }

  useEffect(() => {
    breadcrumbInit()
  }, [breadcrumbInit])

  useEffect(() => {
    queryList()
  }, [queryList])

  return (
    <Spin spinning={loading}>
      <div className={styles.box}>
        <CategoryPicker isButton />
        {breadcrumbItems?.length > 1 && (
          <Breadcrumb items={breadcrumbItems} style={{ marginTop: '15px' }} />
        )}
        {searchData.c && !!moreCategoryList.length && (
          <More
            data={moreCategoryList}
            value={searchData.cc}
            onChange={code => {
              const categoryCode = code === 'all' ? '' : code
              const payload = {
                ...searchData,
                cc: categoryCode,
                w: '',
                type: 'app',
              }
              history.push(`/search?${qs.stringify(payload)}`)
            }}
          />
        )}
        <div className={styles.options}>
          <Sort
            value={+(searchData?.sort || 0)}
            onChange={e => JumpHandle({ sort: e })}
          />
          <Pagination
            page={searchData.page ? +searchData.page : 1}
            onChange={page => {
              JumpHandle({ page })
            }}
            total={total}
            pageSize={pageSize}
          />
        </div>
        {list?.length ? (
          <>
            <div className={styles.list}>
              {list.map(item => (
                <ProductCard
                  key={item.spuCode}
                  data={
                    {
                      spuType: item.spuType,
                      spuCode: item.spuCode,
                      spuName: item.spuName,
                      minSalePrice: item.minSalePrice,
                      maxSalePrice: item.maxSalePrice,
                      appImgUrl: item.appImgUrl,
                    } as SwaggerDTO.商城首页列表商品
                  }
                />
              ))}
            </div>
            <div className={styles.antdPagination}>
              <AntdPagination
                current={searchData.page ? +searchData.page : 1}
                pageSize={20}
                showTotal={total => `共${total}条`}
                showQuickJumper
                showSizeChanger={false}
                total={total}
                onChange={page => {
                  JumpHandle({ page })
                }}
              />
            </div>
          </>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </div>
    </Spin>
  )
}

export default Search
